import React, { useState, useMemo } from "react";
import styled from "styled-components";
import useSWR from "swr";
import {
  Card,
  Input,
  Select,
  Button,
  Tabs,
  Badge,
  Tag,
  Alert,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AdminGuard from "../../components/AdminGuard";
import AdminBatchSynthesisCards from "./AdminBatchSynthesisCards";
import AdminBatchKpiTable from "./AdminBatchKpiTable";
import AdminBatchDetailModal from "./AdminBatchDetailModal";
import { KpiBatchInfoItem } from "../../types/kpiBatchInfo";
import { fetchKpiBatchInfos } from "../../utils/fetcher";
import {
  computeBatchSynthesis,
  formatRangeLabel,
  checkBatchFreshness,
} from "../../utils/kpiBatchUtils";

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 8px 16px 40px 16px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MainTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
  .ant-card-body {
    padding: 12px 16px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const AUTO_REFRESH_OPTIONS = [
  { label: "Actualisation : Désactivée", value: 0 },
  { label: "Actualisation : 30s", value: 30000 },
  { label: "Actualisation : 1 min", value: 60000 },
  { label: "Actualisation : 5 min", value: 300000 },
];

export const AdminBatchKpiContent: React.FC = () => {
  const { t } = useTranslation();

  // Auto-refresh interval
  const [refreshInterval, setRefreshInterval] = useState<number>(0);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<Date>(new Date());

  // Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [selectedRange, setSelectedRange] = useState<string>("ALL");

  // Detail Modal state
  const [selectedItem, setSelectedItem] = useState<KpiBatchInfoItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Data fetching for French KPI batches
  const {
    data: items = [],
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<KpiBatchInfoItem[]>(
    "/batch/kpi-info?countryKey=FRA",
    () => fetchKpiBatchInfos({ countryKey: "FRA" }),
    {
      refreshInterval,
      revalidateOnFocus: true,
      onSuccess: () => {
        setLastUpdatedTime(new Date());
      },
    }
  );

  const referenceDate = useMemo(() => new Date(), []);

  // Computed synthesis
  const synthesis = useMemo(
    () => computeBatchSynthesis(items, referenceDate),
    [items, referenceDate]
  );

  // Dynamic lists from items
  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => {
      if (item.kpiCategory) set.add(item.kpiCategory);
    });
    return Array.from(set).sort();
  }, [items]);

  const availableRanges = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => {
      if (item.range) set.add(item.range);
    });
    return Array.from(set).sort();
  }, [items]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Category filter
      if (selectedCategory !== "ALL" && item.kpiCategory !== selectedCategory) {
        return false;
      }

      // Status filter
      if (selectedStatus === "WARNING") {
        const freshness = checkBatchFreshness(item.runAt, item.range, referenceDate);
        if (!freshness.isWarning) return false;
      } else if (selectedStatus !== "ALL" && item.status !== selectedStatus) {
        return false;
      }

      // Range filter
      if (selectedRange !== "ALL" && item.range !== selectedRange) {
        return false;
      }

      // Search query filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = (item.kpiName || "").toLowerCase().includes(query);
        const matchesCat = (item.kpiCategory || "").toLowerCase().includes(query);
        const matchesRange = (item.range || "").toLowerCase().includes(query);
        const matchesError = (item.error || "").toLowerCase().includes(query);
        if (!matchesName && !matchesCat && !matchesRange && !matchesError) {
          return false;
        }
      }

      return true;
    });
  }, [items, selectedCategory, selectedStatus, selectedRange, searchQuery, referenceDate]);

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    selectedCategory !== "ALL" ||
    selectedStatus !== "ALL" ||
    selectedRange !== "ALL";

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("ALL");
    setSelectedStatus("ALL");
    setSelectedRange("ALL");
  };

  const handleViewItem = (item: KpiBatchInfoItem) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  // Build Tab items
  const tabItems = useMemo(() => {
    const allTabErrors = synthesis.errorCount;
    const allTabWarnings = synthesis.warningCount;
    const allItem = {
      key: "ALL",
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span>{t("admin.tabs.all", "Tous les KPI")}</span>
          <Tag style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
            {synthesis.total}
          </Tag>
          {allTabWarnings > 0 && (
            <Badge
              count={allTabWarnings}
              size="small"
              overflowCount={999}
              style={{ backgroundColor: "#faad14" }}
              title={`${allTabWarnings} ${t("admin.delayed_batches", "batch(s) en retard")}`}
            />
          )}
          {allTabErrors > 0 && (
            <Badge
              count={allTabErrors}
              size="small"
              overflowCount={999}
              style={{ backgroundColor: "#ff4d4f" }}
            />
          )}
        </span>
      ),
    };

    const categoryTabs = availableCategories.map((cat) => {
      const catData = synthesis.categories[cat];
      const count = catData?.total || 0;
      const errors = catData?.errorCount || 0;
      const warnings = catData?.warningCount || 0;

      return {
        key: cat,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span>{cat}</span>
            <Tag style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
              {count}
            </Tag>
            {warnings > 0 && (
              <Badge
                count={warnings}
                size="small"
                overflowCount={999}
                style={{ backgroundColor: "#faad14" }}
                title={`${warnings} ${t("admin.delayed_batches", "batch(s) en retard")}`}
              />
            )}
            {errors > 0 && (
              <Badge
                count={errors}
                size="small"
                overflowCount={999}
                style={{ backgroundColor: "#ff4d4f" }}
              />
            )}
          </span>
        ),
      };
    });

    return [allItem, ...categoryTabs];
  }, [availableCategories, synthesis, t]);

  return (
    <PageContainer>
      {/* Top Header */}
      <PageHeader>
        <HeaderTitles>
          <MainTitle>
            {t("admin.title", "Audit des synchronisations KPI")}
            <Tag color="blue" style={{ fontSize: "12px", fontWeight: "normal", padding: "2px 8px" }}>
              {t("admin.country_badge", "France (FRA)")}
            </Tag>
          </MainTitle>
          <Subtitle>
            {t(
              "admin.subtitle",
              "Supervision de l'état des batchs de synchronisation pour les KPI France"
            )}
          </Subtitle>
        </HeaderTitles>

        <HeaderActions>
          <span style={{ fontSize: "12px", color: "#6b7280" }}>
            {t("admin.last_refresh", "Dernière actualisation")} :{" "}
            <strong>{lastUpdatedTime.toLocaleTimeString("fr-FR")}</strong>
          </span>

          <Select
            size="small"
            value={refreshInterval}
            onChange={(val) => setRefreshInterval(val)}
            options={AUTO_REFRESH_OPTIONS}
            style={{ width: 180 }}
          />

          <Button
            type="primary"
            icon={<ReloadOutlined spin={isValidating} />}
            onClick={() => mutate()}
            loading={isValidating && !isLoading}
          >
            {t("admin.refresh", "Actualiser")}
          </Button>
        </HeaderActions>
      </PageHeader>

      {/* Global API Error Alert */}
      {error && (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message={t("admin.fetch_error", "Impossible de charger les informations de synchronisation des KPI.")}
          description={error.message || String(error)}
          action={
            <Button size="small" danger onClick={() => mutate()}>
              {t("admin.refresh", "Réessayer")}
            </Button>
          }
          style={{ marginBottom: 20 }}
        />
      )}

      {/* Synthesis Cards */}
      <AdminBatchSynthesisCards
        synthesis={synthesis}
        selectedCategory={selectedCategory}
        onSelectCategory={(cat) => setSelectedCategory(cat)}
      />

      {/* Filters Toolbar */}
      <FilterCard>
        <FilterRow>
          <Input
            placeholder={t("admin.filters.search_placeholder", "Rechercher un KPI...")}
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 260 }}
            allowClear
          />

          <Select
            value={selectedCategory}
            onChange={(val) => setSelectedCategory(val)}
            style={{ width: 200 }}
            options={[
              { label: t("admin.filters.all_categories", "Toutes les catégories"), value: "ALL" },
              ...availableCategories.map((c) => ({ label: c, value: c })),
            ]}
          />

          <Select
            value={selectedStatus}
            onChange={(val) => setSelectedStatus(val)}
            style={{ width: 190 }}
            options={[
              { label: t("admin.filters.all_statuses", "Tous les statuts"), value: "ALL" },
              { label: t("admin.filters.status_success", "Succès (SUCCESS)"), value: "SUCCESS" },
              { label: t("admin.filters.status_error", "Erreurs (ERROR)"), value: "ERROR" },
              { label: t("admin.filters.status_warning", "Retards date (Warnings)"), value: "WARNING" },
            ]}
          />

          <Select
            value={selectedRange}
            onChange={(val) => setSelectedRange(val)}
            style={{ width: 200 }}
            options={[
              { label: t("admin.filters.all_ranges", "Toutes les temporalités"), value: "ALL" },
              ...availableRanges.map((r) => ({
                label: `${formatRangeLabel(r)} (${r})`,
                value: r,
              })),
            ]}
          />

          {hasActiveFilters && (
            <Button
              icon={<ClearOutlined />}
              onClick={handleResetFilters}
              style={{ color: "#4b5563" }}
            >
              {t("admin.filters.reset", "Réinitialiser")}
            </Button>
          )}

          <span style={{ marginLeft: "auto", fontSize: "12px", color: "#6b7280" }}>
            <strong>{filteredItems.length}</strong> / {items.length} batchs
          </span>
        </FilterRow>
      </FilterCard>

      {/* Category Tabs */}
      <Tabs
        activeKey={selectedCategory}
        onChange={(key) => setSelectedCategory(key)}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      {/* Main Table */}
      <AdminBatchKpiTable
        items={filteredItems}
        loading={isLoading}
        referenceDate={referenceDate}
        onViewItem={handleViewItem}
      />

      {/* Detail / Error Modal */}
      <AdminBatchDetailModal
        item={selectedItem}
        open={modalOpen}
        referenceDate={referenceDate}
        onClose={() => {
          setModalOpen(false);
          setSelectedItem(null);
        }}
      />
    </PageContainer>
  );
};

const AdminPage: React.FC = () => {
  return (
    <AdminGuard>
      <AdminBatchKpiContent />
    </AdminGuard>
  );
};

export default AdminPage;
